import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { WHATSAPP_URL } from "../lib/site";
import { EASE } from "./Reveal";

const line = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: 0,
        transition: { duration: 1.2, delay: 0.35 + i * 0.14, ease: EASE },
    }),
};

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

    return (
        <section ref={ref} className="relative h-[100svh] min-h-[620px] overflow-hidden bg-noir" data-testid="hero-section">
            <motion.div className="absolute inset-0" style={{ y }} data-testid="hero-parallax-image">
                <motion.img
                    src="https://customer-assets-4nw71qhi.emergentagent.net/job_bath-sanctuary/artifacts/vjojuftr_ChatGPT%20Image%20Sep%2021%2C%202026%2C%2002_17_37%20PM.png"
                    alt="Bright green-tiled luxury bathroom with freestanding tub and garden view"
                    className="h-full w-full object-cover img-treated blur-[2.5px]"
                    initial={{ scale: 1.18 }}
                    animate={{ scale: 1.04 }}
                    transition={{ duration: 2.6, ease: EASE }}
                />
                <div className="absolute inset-0 bg-noir/20" />
            </motion.div>

            <motion.div style={{ opacity }} className="relative z-10 h-full">
                <div className="mx-auto flex h-full max-w-[1440px] items-end px-4 pb-6 sm:px-10 sm:pb-12">
                    <div
                        className="w-full rounded-[1.75rem] sm:rounded-[2.5rem] border border-white/25 bg-noir/25 backdrop-blur-2xl p-6 sm:p-12 shadow-[0_25px_80px_rgba(0,0,0,0.35)]"
                        data-testid="hero-glass-panel"
                    >
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 0.2 }}
                        className="mb-5 font-mono text-[10px] sm:text-xs tracking-[0.3em] uppercase text-brass drop-shadow-[0_1px_6px_rgba(0,0,0,0.8)]"
                        data-testid="hero-eyebrow"
                    >
                        A Spaces by Samita Venture &mdash; Bangalore
                    </motion.p>

                    <h1 className="font-serif font-light text-white leading-[1.04] tracking-tight text-[clamp(2.2rem,6.5vw,5.6rem)]">
                        {["India builds beautiful homes.", "Then forgets the one room", "that rejuvenates you."].map(
                            (l, i) => (
                                <span key={i} className="block overflow-hidden pb-[0.09em] -mb-[0.09em]">
                                    <motion.span
                                        className="block will-change-transform"
                                        variants={line}
                                        custom={i}
                                        initial="hidden"
                                        animate="show"
                                    >
                                        {i === 2 ? (
                                            <>
                                                that <em className="italic text-brass">rejuvenates</em> you.
                                            </>
                                        ) : (
                                            l
                                        )}
                                    </motion.span>
                                </span>
                            )
                        )}
                    </h1>

                    <div className="mt-8 flex flex-col gap-8 sm:mt-10 sm:flex-row sm:items-end sm:justify-between">
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, delay: 1.15, ease: EASE }}
                            className="max-w-xl text-sm sm:text-base font-light leading-relaxed text-white/75"
                            data-testid="hero-subline"
                        >
                            Bath & Beyond turns tired, purely functional bathrooms into premium
                            personal sanctuaries &mdash; design, fittings, water, warmth and light,
                            all considered. All by one team. Premium bathroom renovation in
                            Bangalore, done turnkey.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, delay: 1.3, ease: EASE }}
                            className="flex flex-wrap items-center gap-4"
                        >
                            <a
                                data-testid="hero-whatsapp-cta"
                                href={WHATSAPP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="rounded-full bg-brass px-7 py-4 text-sm font-semibold text-noir transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(212,175,55,0.35)]"
                            >
                                Plan my renovation
                            </a>
                        </motion.div>
                    </div>
                    </div>
                </div>
            </motion.div>
        </section>
    );
};

export default Hero;
